"use client";

import { Crown, Heart } from "lucide-react";
import Link from "@/components/ui/Link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { TimeAgo } from "@/components/ui/TimeAgo";
import type { VideoSummary } from "@/lib/api/types";
import { thumbBackground } from "@/lib/art";
import { cn } from "@/lib/cn";
import { compact, duration } from "@/lib/format";
import { previewFrames } from "@/lib/frame-sources";
import { useUi } from "@/components/ui/use-ui";
import { fmt } from "@/lib/locales/fmt";
import { quietData, useScrollPreview } from "./card-preview";

export const videoHref = (id: string) => `/-${id}`;

export interface CardProgress {
  /** Seconds into the video where playback stopped. */
  position: number;
  duration: number;
  finished: boolean;
  /** When it was watched, in ms. */
  at: number;
}

/**
 * `progress` (on Watching) adds a progress bar and "Stopped at …" in place of the stats;
 * `action` is a button at the end of the caption.
 */
/** `priority`: among the first on screen, so its thumbnail is fetched ahead of the rest. */
export function VideoCard({
  video,
  eager,
  priority,
  progress,
  action,
}: {
  video: VideoSummary;
  eager?: boolean;
  priority?: boolean;
  progress?: CardProgress;
  action?: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  // On phones: the card in the middle of the screen while the visitor scrolls (card-preview.ts).
  const [centered, setCentered] = useState(false);
  // Neither its clip nor its stills could be had: it gives up its turn in the middle of the screen.
  const [nothing, setNothing] = useState(false);
  const t = useUi();
  const timer = useRef<number | undefined>(undefined);
  const box = useRef<HTMLAnchorElement>(null);
  const href = videoHref(video.id);
  const creator = video.creator;
  const frames = useMemo(() => previewFrames(video.thumb), [video.thumb]);
  useScrollPreview(box, !nothing && !!(video.preview || frames), setCentered);

  // Pointed at with a mouse for a moment (sweeping across the grid starts none of the cards passed over),
  // or reached with the keyboard; a finger's touch opens the video instead.
  const start = (e: React.PointerEvent | React.FocusEvent<HTMLElement>) => {
    if ("pointerType" in e ? e.pointerType === "touch" : !e.currentTarget.matches(":focus-visible")) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHover(true), HOVER_MS);
  };
  const stop = () => {
    window.clearTimeout(timer.current);
    setHover(false);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const gaveUp = useCallback(() => setNothing(true), []);

  return (
    <article className="group/card min-w-0">
      <Link
        ref={box}
        href={href}
        onPointerEnter={start}
        onPointerLeave={stop}
        onFocus={start}
        onBlur={stop}
        className="relative block aspect-video overflow-hidden rounded-lg bg-surface ring-1 ring-white/[0.04]"
      >
        <Thumb video={video} frames={frames} playing={hover || centered} pointed={hover} bar={!progress} eager={eager} priority={priority} onNothing={gaveUp} />
        {video.premium && (
          <span className="theme-dark glass absolute left-2 top-2 flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium text-brand-soft">
            <Crown size={12} /> {t.nav.premium}
          </span>
        )}
        <span className="thumb-text absolute right-2.5 top-2 text-[13px] tracking-wide text-white">
          {video.label && <span className="mr-1">{video.label}</span>}
          {duration(video.duration)}
        </span>
        {progress && (
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-white/25">
            <span
              className="block h-full bg-brand"
              style={{ width: `${progress.finished ? 100 : Math.min(100, (progress.position / (progress.duration || video.duration || 1)) * 100)}%` }}
            />
          </span>
        )}
      </Link>

      <div className="mt-3 flex gap-3">
        {creator ? (
          <Link href={`/${creator.slug}`} className="shrink-0" aria-label={creator.name} tabIndex={-1}>
            <Avatar name={creator.name} src={creator.avatar} size={36} variant={creator.kind === "brand" ? "brand" : "person"} />
          </Link>
        ) : (
          // beeg shows an empty circle when a video has no uploader.
          <span aria-hidden className="size-9 shrink-0 rounded-full bg-white/[0.06]" />
        )}
        <div className="min-w-0 flex-1 leading-snug">
          {(creator || video.brand) && (
            <p className="truncate text-[14px] text-fg">
              {creator && (
                <Link href={`/${creator.slug}`} className="hover:underline hover:underline-offset-2">
                  {creator.name}
                </Link>
              )}
              {creator && video.brand && <span className="mx-1.5 text-faint">·</span>}
              {video.brand && (
                <Link href={`/${video.brand.slug}`} className="text-muted hover:text-soft">
                  {video.brand.name}
                </Link>
              )}
            </p>
          )}
          <Link href={href} className="mt-0.5 block truncate text-[14px] text-soft transition-colors hover:text-fg" title={video.title}>
            {video.title}
          </Link>
          {progress ? (
            <p className="mt-1.5 flex items-center gap-3 text-[12px] text-muted">
              <span className={cn("tabular-nums", !progress.finished && "text-soft")}>
                {progress.finished
                  ? t.video.watchedToEnd
                  : fmt(t.video.stoppedAt, { at: duration(progress.position), of: duration(progress.duration || video.duration) })}
              </span>
              <TimeAgo iso={new Date(progress.at).toISOString()} />
            </p>
          ) : (
            // Counts only where there are some: feeds without view counts (lib/frame-sources noViews) leave 0.
            <p className="mt-1.5 flex items-center gap-3 text-[12px] text-muted">
              {video.views > 0 && <span>{compact(video.views)}</span>}
              <TimeAgo iso={video.publishedAt} />
              {video.likes > 0 && (
                <span className="flex items-center gap-1">
                  <Heart size={12} strokeWidth={2} />
                  {compact(video.likes)}
                </span>
              )}
            </p>
          )}
        </div>
        {action}
      </div>
    </article>
  );
}

/** Before a card pointed at starts its preview. */
const HOVER_MS = 150;

/**
 * The thumbnail and, while `playing`, the video's moments over it: its preview clip (a studio's trailer only
 * when `pointed` at with a mouse: too heavy for phones' data), or failing that its stills, or nothing (the
 * thumbnail only grows a little). `onNothing`: neither could be had.
 */
function Thumb({
  video,
  frames,
  playing,
  pointed,
  bar,
  eager,
  priority,
  onNothing,
}: {
  video: VideoSummary;
  frames: string[] | null;
  playing: boolean;
  pointed: boolean;
  bar: boolean;
  eager?: boolean;
  priority?: boolean;
  onNothing: () => void;
}) {
  // A thumbnail the source no longer has gives way to the gradient art, not a broken image.
  const [failed, setFailed] = useState(false);
  const [clipFailed, setClipFailed] = useState(false);
  const [stillsFailed, setStillsFailed] = useState(false);
  const clip = !clipFailed ? (video.preview ?? (pointed ? video.trailer : null)) : null;
  const stills = !clip && !failed && !stillsFailed ? frames : null;
  // Visitors saving data (or on a 2G connection) get the thumbnail only.
  const moments = playing && !quietData();
  const noClip = useCallback(() => {
    setClipFailed(true);
    if (!frames) onNothing();
  }, [frames, onNothing]);
  const noStills = useCallback(() => {
    setStillsFailed(true);
    onNothing();
  }, [onNothing]);
  return (
    <>
      {video.thumb && !failed ? (
        // eslint-disable-next-line @next/next/no-img-element -- thumbnails are served by the content CDN
        <img
          src={video.thumb}
          alt={video.title}
          loading={eager || priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          onError={() => setFailed(true)}
          className={cn("size-full object-cover transition-transform duration-500", playing && "scale-[1.03]")}
        />
      ) : (
        <span
          role="img"
          aria-label={video.title}
          className={cn("block size-full transition-transform duration-[1.2s] ease-out", playing ? "scale-110" : "scale-100")}
          style={{ backgroundImage: thumbBackground(video.id) }}
        />
      )}
      {moments && clip && <Clip src={clip} bar={bar} onFail={noClip} />}
      {moments && stills && <Stills frames={stills} bar={bar} onFail={noStills} />}
    </>
  );
}

/** The line along a card's bottom while its preview plays. */
function PreviewBar() {
  return (
    <span aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-white/15">
      <span className="block h-full origin-left animate-[preview_8s_linear_forwards] bg-brand" />
    </span>
  );
}

/** A clip longer than this is a studio's whole trailer, played from a tenth of the way in (past its intro). */
const LONG_CLIP_S = 20;

/**
 * A preview clip (a tube's few seconds of highlights, or a studio's trailer), muted and looping over the
 * thumbnail, which shows until the clip's first frame does. Nothing is fetched until it's mounted, and its
 * download stops when it's unmounted.
 */
function Clip({ src, bar, onFail }: { src: string; bar: boolean; onFail: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.src = src;
    // Refused (iOS in Low Power Mode plays nothing by itself): the stills instead, where there are some.
    v.play().catch((e: unknown) => {
      if (!(e instanceof DOMException && e.name === "AbortError")) onFail();
    });
    return () => {
      v.pause();
      v.removeAttribute("src");
      v.load();
    };
  }, [src, onFail]);
  return (
    <>
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.duration > LONG_CLIP_S && v.currentTime === 0) v.currentTime = v.duration / 10;
        }}
        onPlaying={() => setShown(true)}
        onError={(e) => {
          // Not the emptying on unmount: the clip itself failed.
          if (e.currentTarget.getAttribute("src")) onFail();
        }}
        className={cn("absolute inset-0 size-full scale-[1.03] object-cover transition-opacity duration-300", !shown && "opacity-0")}
      />
      {bar && shown && <PreviewBar />}
    </>
  );
}

const STILL_MS = 700;

/**
 * For videos with stills instead of a clip (Pornhub's 16, XVideos' 30, FlyFLV's: lib/frame-sources
 * previewFrames): flips through them once the first has come. A source with fewer than asked for ends at
 * the first missing one; one without any (the first missing) has none.
 */
function Stills({ frames, bar, onFail }: { frames: string[]; bar: boolean; onFail: () => void }) {
  const [i, setI] = useState(0);
  const [count, setCount] = useState(frames.length);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!shown || count < 2) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % count), STILL_MS);
    return () => window.clearInterval(t);
  }, [shown, count]);
  const next = (i + 1) % count;
  const missing = (n: number) => {
    if (n === 0) onFail();
    else {
      setCount((c) => Math.min(c, n));
      setI((m) => (m >= n ? 0 : m));
    }
  };
  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element -- stills come from the source's CDN */}
      <img
        src={frames[i]}
        alt=""
        decoding="async"
        onLoad={() => setShown(true)}
        onError={() => missing(i)}
        className={cn("absolute inset-0 size-full scale-[1.03] object-cover", !shown && "opacity-0")}
      />
      {/* The next one, fetched ahead so the flip doesn't flash. */}
      {next !== i && (
        // eslint-disable-next-line @next/next/no-img-element -- as above
        <img src={frames[next]} alt="" aria-hidden onError={() => missing(next)} className="hidden" />
      )}
      {bar && shown && <PreviewBar />}
    </>
  );
}

export function VideoCardSkeleton() {
  return (
    <div aria-hidden className="min-w-0">
      <div className="skeleton aspect-video rounded-lg" />
      <div className="mt-3 flex gap-3">
        <div className="skeleton size-9 shrink-0 rounded-full" />
        <div className="flex-1 space-y-2 pt-0.5">
          <div className="skeleton h-3.5 w-2/5 rounded" />
          <div className="skeleton h-3.5 w-4/5 rounded" />
          <div className="skeleton h-3 w-1/3 rounded" />
        </div>
      </div>
    </div>
  );
}
